// src/pages/ResetCommissionPage/ResetCommissionPage.jsx

import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
import {jwtDecode} from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const ResetCommissionPage = () => {
  const { shopId } = useParams();
  const [currentCommission, setCurrentCommission] = useState(null);
  const [currentMinimumPay, setCurrentMinimumPay] = useState(null);
  const [currentCashbackCount, setCurrentCashbackCount] = useState(null);
  const [newCommission, setNewCommission] = useState('');
  const [newMinimumPay, setNewMinimumPay] = useState('');
  // Optional maximum pay for game cards
  const [currentMaxEnabled, setCurrentMaxEnabled] = useState(false);
  const [currentMaximumPay, setCurrentMaximumPay] = useState(0);
  const [newMaxEnabled, setNewMaxEnabled] = useState(false);
  const [newMaximumPay, setNewMaximumPay] = useState('');
  const [newCashbackCount, setNewCashbackCount] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  const navigate = useNavigate();
  const { can } = usePermissions();
  const canEditCommission = can('merchants.commission.edit');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.userId);
        fetchCurrentData(decodedToken.userId);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error decoding token. Please login again.',
          severity: 'error',
        });
        navigate('/login');
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Authentication required. Please login.',
        severity: 'error',
      });
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentData = async (id) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/shop-important-data/${shopId}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.shopImportantData) {
        const { commissionRate, minimumPay, cashbackCount, maximumPayEnabled, maximumPay } = response.data.shopImportantData;
        setCurrentMaxEnabled(Boolean(maximumPayEnabled));
        setCurrentMaximumPay(maximumPay || 0);
        setNewMaxEnabled(Boolean(maximumPayEnabled));
        setNewMaximumPay('');
        setCurrentCommission(commissionRate);
        setCurrentMinimumPay(minimumPay);
        setCurrentCashbackCount(cashbackCount);
      } else {
        setSnackbar({
          open: true,
          message: 'No data available for the shop.',
          severity: 'info',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error fetching current data.',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  const handleSubmit = () => {
    if (!canEditCommission) return;
    setOpenConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setOpenConfirmDialog(false);
    if (!canEditCommission) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to reset commissions.',
        severity: 'error',
      });
      return;
    }
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const payload = {};
      if (newCommission) payload.commissionRate = parseFloat(newCommission);
      if (newMinimumPay) payload.minimumPay = parseFloat(newMinimumPay);
      if (newCashbackCount) payload.cashbackCount = parseInt(newCashbackCount);
      if (newMaxEnabled !== currentMaxEnabled) payload.maximumPayEnabled = newMaxEnabled;
      if (newMaximumPay) payload.maximumPay = parseFloat(newMaximumPay);

      const response = await axios.put(
        `${API_BASE_URL}/shop-important-data/${shopId}/${userId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: 'Data updated successfully.',
          severity: 'success',
        });
        setNewCommission('');
        setNewMinimumPay('');
        setNewCashbackCount('');
        setNewMaximumPay('');
        fetchCurrentData(userId);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to update data.',
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating data.',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <CircularProgress color="primary" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
        <Box sx={{ position: 'relative', padding: 2 }}>
              {/* Place BackButton outside the Grid */}
              <BackButton />
              <br/>  <br/>
      <Typography variant="h4">Reset Commission</Typography>
      <Typography variant="h6" style={{ marginTop: '20px' }}>
        Current Commission Rate: {currentCommission ?? 'Not available'}
      </Typography>
      <Typography variant="h6" style={{ marginTop: '10px' }}>
        Current Minimum Pay: {currentMinimumPay ?? 'Not available'}
      </Typography>
      <Typography variant="h6" style={{ marginTop: '10px' }}>
        Current Cashback Count: {currentCashbackCount ?? 'Not available'}
      </Typography>
      <Typography variant="h6" style={{ marginTop: '10px' }}>
        Current Maximum Pay: {currentMaxEnabled ? `₹${currentMaximumPay} (Active)` : 'Inactive'}
      </Typography>
      <TextField
        label="New Commission Rate (%)"
        value={newCommission}
        onChange={(e) => setNewCommission(e.target.value)}
        type="number"
        fullWidth
        style={{ marginTop: '20px' }}
      />
      <TextField
        label="New Minimum Pay"
        value={newMinimumPay}
        onChange={(e) => setNewMinimumPay(e.target.value)}
        type="number"
        fullWidth
        style={{ marginTop: '20px' }}
      />
      {/* Optional maximum pay: above this amount the customer gets no game card */}
      <Box sx={{ mt: 2.5, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: newMaxEnabled ? '#fff7ed' : 'transparent' }}>
        <FormControlLabel
          control={<Switch checked={newMaxEnabled} onChange={(e) => setNewMaxEnabled(e.target.checked)} color="warning" />}
          label={`Maximum Pay ${newMaxEnabled ? '(Active)' : '(Inactive)'}`}
        />
        <TextField
          label={`New Maximum Pay${currentMaxEnabled ? ` (current ₹${currentMaximumPay})` : ''}`}
          value={newMaximumPay}
          onChange={(e) => setNewMaximumPay(e.target.value)}
          type="number"
          fullWidth
          disabled={!newMaxEnabled}
          sx={{ mt: 1 }}
          helperText="When active: payments above this amount get no game card"
        />
      </Box>
      <TextField
        label="New Cashback Count"
        value={newCashbackCount}
        onChange={(e) => setNewCashbackCount(e.target.value)}
        type="number"
        fullWidth
        style={{ marginTop: '20px' }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!canEditCommission || (!newCommission && !newMinimumPay && !newCashbackCount && !newMaximumPay && newMaxEnabled === currentMaxEnabled)}
        style={{ marginTop: '20px' }}
      >
        Submit
      </Button>
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the shop data?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      </Box>
    </div>
  );
};

export default ResetCommissionPage;
